import { HttpClient } from '@angular/common/http';
import {
	ChangeDetectionStrategy,
	Component,
	inject,
	Input,
	OnChanges,
	OnDestroy,
	signal,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subscription, catchError, map, of } from 'rxjs';

/**
 * Carga un SVG desde `assets` y lo inserta en el DOM (no usa &lt;img&gt;).
 * Así las variables CSS del tema (`var(--pwcs-icon-border)`, etc.) se resuelven en el gráfico.
 * Opcionalmente reescribe `id` y `url(#…)` para evitar colisiones entre varias instancias.
 */
@Component({
	selector: 'pwcs-inline-svg',
	standalone: true,
	template: `
		@if (svgHtml(); as html) {
			<div
				[attr.class]="wrapperClass"
				[attr.role]="ariaRole"
				[attr.aria-label]="ariaLabel"
				[attr.aria-hidden]="ariaHidden"
				[innerHTML]="html"
			></div>
		}
	`,
	styleUrl: './pwcs-inline-svg.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PwcsInlineSvgComponent implements OnChanges, OnDestroy {
	private readonly http = inject(HttpClient);
	private readonly sanitizer = inject(DomSanitizer);

	private requestSub: Subscription | undefined;

	/** Ruta al recurso (p. ej. `assets/images/home-ocr.svg`). */
	@Input({ required: true }) src!: string;
	/** Sufijo para hacer únicos los `id` del SVG (p. ej. índice del stepper). */
	@Input() idSuffix: string | number | null = null;
	/** Clases CSS adicionales en el contenedor (p. ej. `pwcs-stepper-svg--ocr`). */
	@Input() cssClass = '';
	/** Texto alternativo para accesibilidad. Equivalente al `alt` de `<img>`. */
	@Input() alt = '';

	protected readonly svgHtml = signal<SafeHtml | null>(null);

	protected get wrapperClass(): string {
		return ['pwcs-inline-svg', this.cssClass].filter((c) => c && c.length > 0).join(' ');
	}

	protected get resolvedAlt(): string {
		const customAlt = this.alt.trim();
		if (customAlt) {
			return customAlt;
		}
		return inferAltFromSrc(this.src);
	}

	protected get ariaRole(): 'img' | null {
		return this.resolvedAlt ? 'img' : null;
	}

	protected get ariaLabel(): string | null {
		return this.resolvedAlt || null;
	}

	protected get ariaHidden(): 'true' | null {
		return this.resolvedAlt ? null : 'true';
	}

	ngOnChanges(): void {
		this.load();
	}

	private load(): void {
		this.requestSub?.unsubscribe();

		if (!this.src?.trim()) {
			this.svgHtml.set(null);
			return;
		}

		this.requestSub = this.http
			.get(this.src, { responseType: 'text' as const })
			.pipe(
				map((raw: string) =>
					this.idSuffix !== null && this.idSuffix !== undefined ? uniquifySvgIds(raw, String(this.idSuffix)) : raw,
				),
				map((html: string) => this.sanitizer.bypassSecurityTrustHtml(html)),
				catchError(() => of<SafeHtml | null>(null)),
			)
			.subscribe((safe) => this.svgHtml.set(safe));
	}

	ngOnDestroy(): void {
		this.requestSub?.unsubscribe();
	}
}

/**
 * Evita colisiones de `id` al repetir el mismo asset en la vista.
 * Reescribe `url(#id)`, `xlink:href="#id"` y `href="#id"` (patterns Figma con PNG embebido).
 */
export function uniquifySvgIds(svg: string, suffix: string): string {
	const safeSuffix = suffix.replace(/[^a-zA-Z0-9_-]/g, '');
	if (safeSuffix === '') {
		return svg;
	}

	const ids = new Set<string>();
	const idAttr = /\bid="([^"]+)"/g;
	let m: RegExpExecArray | null;
	while ((m = idAttr.exec(svg)) !== null) {
		ids.add(m[1]);
	}

	let result = svg;
	const sorted = Array.from(ids).sort((a, b) => b.length - a.length);
	for (const oldId of sorted) {
		const newId = `${oldId}--${safeSuffix}`;
		result = result.split(`id="${oldId}"`).join(`id="${newId}"`);
		result = result.split(`url(#${oldId})`).join(`url(#${newId})`);
		// Antes que href="#": evita dejar xlink:href rotos tras renombrar <image id>.
		result = result.split(`xlink:href="#${oldId}"`).join(`xlink:href="#${newId}"`);
		result = result.split(`href="#${oldId}"`).join(`href="#${newId}"`);
	}
	return result;
}

function inferAltFromSrc(src: string): string {
	if (!src?.trim()) {
		return '';
	}

	const pathPart = src.split('?')[0];
	const filename = pathPart.split('/').pop() ?? '';
	const withoutExtension = filename.replace(/\.svg$/i, '').trim();
	if (!withoutExtension) {
		return '';
	}

	return withoutExtension.replace(/[-_]+/g, ' ').trim();
}
